import { Button, Card, Modal, Select, Space, message } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { File, MonitorEarlyWarnRecord } from "@zhihaoscm/entity";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { BaseItem as ZhBaseItem, DetailHeader as ZhDetailHeader, Grid as ZhGrid } from "zhihao-ui-react";

import { shippingRequestHttp } from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { EXCEPTION_TYPE, HANDLE_STATE, RECOGNIZE_RESULT } from "@/meta/logistics/monitorWarn";
import { WAYBILL_STATE } from "@/meta/logistics/waybill";
import { useUserStore } from "@/stores/react/authStore";
import { showRequestError } from "@/utils/requestErrorMessage";

import type { MonitorWarnOfficer } from "./MonitorWarnPage";
import { notifyOperationSuccess } from "@/utils/operationFeedback";

export interface MonitorWarnDetailPageProps {
    warningId?: number | string;
    currentUserName?: string;
    loadDetail?: (id: number) => Promise<MonitorEarlyWarnRecord>;
    loadFile?: (id: number) => Promise<File>;
    loadCommissioners?: () => Promise<MonitorWarnOfficer[]>;
    handleWarning?: (form: { id: number; misinformation: number; remark: string }) => Promise<unknown>;
    assignWarning?: (id: number, userId: number) => Promise<unknown>;
    permissions?: string[];
}
export async function loadMonitorWarnDetail(id: number) {
    const response = await shippingRequestHttp.service.get<MonitorEarlyWarnRecord>(`/monitor-early-warn-record/${id}`);
    return response.data;
}
export async function loadMonitorWarnFile(id: number) {
    const response = await shippingRequestHttp.service.get<File>(`/dataService/admin/file/${id}`);
    return response.data;
}
export async function handleMonitorWarn(form: { id: number; misinformation: number; remark: string }) {
    const response = await shippingRequestHttp.service.put("/monitor-early-warn-record/handle", form);
    return response.data;
}
export async function assignMonitorWarn(id: number, userId: number) {
    const response = await shippingRequestHttp.service.put(`/monitor-early-warn-record/assign/${id}/${userId}`);
    return response.data;
}

const text = (value: unknown) => (value == null || value === "" ? DEFAULT_VALUE : String(value));
const enumText = (meta: { getIntroByCode: (code?: number | string) => string }, value?: number) =>
    meta.getIntroByCode(value) || DEFAULT_VALUE;

/** 复用旧 Vue 详情页的货仓状态文案，并保留装卸剩余时间提示。 */
const warehouseStateText = (value: number | undefined, remainingLayTime?: number) => {
    const state = enumText(RECOGNIZE_RESULT, value);
    return value === RECOGNIZE_RESULT.LOADING.code || value === RECOGNIZE_RESULT.UNLOADING.code
        ? `${state}（预计剩余${text(remainingLayTime)}h）`
        : state;
};

type CoordinateValue = number | string | null | undefined;
type MonitorWarnLocationData = Partial<MonitorEarlyWarnRecord> & {
    /** 兼容旧接口可能直接返回的经纬度字段。 */
    lng?: CoordinateValue;
    lat?: CoordinateValue;
    longitude?: CoordinateValue;
    latitude?: CoordinateValue;
    lngLat?: string;
    location?: {
        lng?: CoordinateValue;
        lat?: CoordinateValue;
        longitude?: CoordinateValue;
        latitude?: CoordinateValue;
    };
};

interface NormalizedLocation {
    state: "available" | "missing" | "invalid";
    coordinate?: string;
}

type CoordinateSource = {
    lng?: CoordinateValue;
    lat?: CoordinateValue;
    longitude?: CoordinateValue;
    latitude?: CoordinateValue;
};

/** 将 Vue 详情接口的多种坐标形态统一为地图入口所需的 lng, lat 文本。 */
const normalizeLocation = (data: MonitorWarnLocationData): NormalizedLocation => {
    const source = (data.geo || data.location) as CoordinateSource | undefined;
    const lng = source?.lng ?? source?.longitude ?? data.lng ?? data.longitude;
    const lat = source?.lat ?? source?.latitude ?? data.lat ?? data.latitude;
    if (lng !== undefined || lat !== undefined) {
        if (lng === null || lat === null || String(lng).trim() === "" || String(lat).trim() === "") {
            return { state: "invalid" };
        }
        const lngNumber = Number(lng);
        const latNumber = Number(lat);
        return Number.isFinite(lngNumber) && Number.isFinite(latNumber)
            ? { state: "available", coordinate: `${lng}, ${lat}` }
            : { state: "invalid" };
    }
    if (data.lngLat?.trim()) {
        const values = data.lngLat.split(/[，,\s]+/).filter(Boolean);
        if (values.length === 2 && values.every(value => Number.isFinite(Number(value)))) {
            return { state: "available", coordinate: `${values[0]}, ${values[1]}` };
        }
        return { state: "invalid" };
    }
    return { state: "missing" };
};

/** 位置卡片保留 Vue MapDialog 的关键信息，并用外部地图链接作为无 SDK 降级入口。 */
function MonitorWarnLocationCard({ detail }: { detail: MonitorWarnLocationData }) {
    const location = normalizeLocation(detail);
    const isShip = Number(detail.type) === EXCEPTION_TYPE.SHIP.code;
    const subject = isShip ? text(detail.shipCnName || detail.shipName) : `设备 ${text(detail.deviceSerialNo)}`;
    const mapUrl = location.coordinate
        ? `https://uri.amap.com/marker?position=${encodeURIComponent(location.coordinate)}`
        : undefined;

    return (
        <Card title="位置与联动" size="small" className="mt-4">
            <ZhGrid cols={2} gap={[30, 20]}>
                <ZhBaseItem label="关联对象">
                    <span>{isShip ? `关联船舶：${subject}` : `关联设备：${subject}`}</span>
                </ZhBaseItem>
                <ZhBaseItem label="告警坐标">
                    {location.state === "available" ? (
                        <span>{location.coordinate}</span>
                    ) : location.state === "invalid" ? (
                        <span role="status">位置数据异常</span>
                    ) : (
                        <span role="status">暂无坐标</span>
                    )}
                </ZhBaseItem>
                {detail.fileId ? (
                    <ZhBaseItem label="抓拍文件">
                        <span>图片文件 ID：{detail.fileId}</span>
                    </ZhBaseItem>
                ) : null}
            </ZhGrid>
            {mapUrl ? (
                <Button className="mt-4" type="link" href={mapUrl} target="_blank" rel="noreferrer">
                    在地图中查看
                </Button>
            ) : (
                <Button className="mt-4" type="link" disabled>
                    在地图中查看
                </Button>
            )}
        </Card>
    );
}

/** 预警详情只在显式进入后读取详情；图片请求依赖详情返回的 fileId，避免提前请求。 */
export function MonitorWarnDetailPage({
    warningId,
    currentUserName,
    loadDetail = loadMonitorWarnDetail,
    loadFile = loadMonitorWarnFile,
    loadCommissioners,
    handleWarning = handleMonitorWarn,
    assignWarning = assignMonitorWarn,
    permissions
}: MonitorWarnDetailPageProps) {
    const params = useParams<{ id: string }>();
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    const storeUserName = useUserStore(state => state.userInfo.user?.name || "");
    // 保持 Vue 详情页 v-auth 语义：显式测试权限优先，否则读取当前登录用户权限。
    const currentPermissions = permissions ?? storePermissions;
    const canAssign = currentPermissions.includes("ROLE_WARNING_MANAGE");
    const canHandlePermission = currentPermissions.includes("ROLE_WARNING_DEAL");
    const effectiveUserName = currentUserName ?? storeUserName;
    const id = warningId ?? params.id;
    const [detail, setDetail] = useState<MonitorEarlyWarnRecord>();
    const [image, setImage] = useState<string>("");
    const [errorMessage, setErrorMessage] = useState("");
    const [assignOpen, setAssignOpen] = useState(false);
    const [officers, setOfficers] = useState<MonitorWarnOfficer[]>([]);
    const [selectedOfficer, setSelectedOfficer] = useState<number>();
    const [handleOpen, setHandleOpen] = useState(false);
    const [handleError, setHandleError] = useState("");
    const [remark, setRemark] = useState("");
    const [misinformation, setMisinformation] = useState(0);
    const [loading, setLoading] = useState(false);
    /** 成功动作后重新读取详情，保持处理状态与指派人和 Vue 页面一致。 */
    const refreshDetail = async () => {
        if (!id) return;
        const result = await loadDetail(Number(id));
        setDetail(result);
    };
    useEffect(() => {
        if (!id) return;
        let disposed = false;
        setDetail(undefined);
        setImage("");
        setErrorMessage("");
        void loadDetail(Number(id))
            .then(result => {
                if (disposed) return;
                setDetail(result);
                if (result?.fileId)
                    void loadFile(result.fileId)
                        .then(file => {
                            if (!disposed) setImage(file?.path || "");
                        })
                        .catch(() => {
                            if (!disposed) setImage("");
                        });
            })
            .catch(() => {
                if (!disposed) setErrorMessage("预警详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [id, loadDetail, loadFile]);
    const submitAssign = async () => {
        if (!detail?.id || !selectedOfficer) return;
        setLoading(true);
        try {
            await assignWarning(detail.id, selectedOfficer);
            await refreshDetail();
            notifyOperationSuccess("指派成功");
            setAssignOpen(false);
        } catch (error) {
            showRequestError(error, "指派失败");
        } finally {
            setLoading(false);
        }
    };
    const submitHandle = async () => {
        if (!detail?.id) return;
        setHandleError("");
        if (!remark.trim() && misinformation === 0) {
            message.error("请输入备注");
            return;
        }
        setLoading(true);
        try {
            await handleWarning({ id: detail.id, misinformation, remark: remark.trim() });
            await refreshDetail();
            notifyOperationSuccess("处理成功");
            setHandleOpen(false);
        } catch {
            setHandleError("处理失败");
        } finally {
            setLoading(false);
        }
    };
    const canHandle =
        canHandlePermission &&
        detail?.handleState === HANDLE_STATE.UNHANDLED.code &&
        effectiveUserName === detail.handlerName;
    if (!detail)
        return (
            <section aria-label="monitor-warn-detail-page">
                <Card>{errorMessage ? <Alert role="alert" message={errorMessage} type="error" /> : "加载中..."}</Card>
            </section>
        );
    return (
        <section aria-label="monitor-warn-detail-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <ZhDetailHeader
                    leftInfos={[{ label: "编号", value: String(detail.id) }]}
                    stateDesc={enumText(HANDLE_STATE, detail.handleState)}
                    title="预警详情"
                />
                <div className="mb-4 mt-6 text-base font-medium">基本信息</div>
                <ZhGrid cols={3} gap={[30, 20]}>
                    <ZhBaseItem label="类型" value={<span>{enumText(EXCEPTION_TYPE, detail.type)}</span>} />
                    <ZhBaseItem label="触发时间" value={<span>{text(detail.triggerTime?.replace("T", " "))}</span>} />
                    {Number(detail.type) === EXCEPTION_TYPE.DEVICE.code ? (
                        <ZhBaseItem label="设备序列号" value={<span>{text(detail.deviceSerialNo)}</span>} />
                    ) : null}
                </ZhGrid>
                {Number(detail.type) === EXCEPTION_TYPE.SHIP.code ? (
                    <>
                        <div className="mb-4 mt-6 text-base font-medium">预警信息</div>
                        <ZhGrid cols={3} gap={[30, 20]}>
                            <ZhBaseItem
                                label="预期货仓状态"
                                value={
                                    <span>
                                        {detail.expectWarehouseState
                                            ?.map(item => enumText(RECOGNIZE_RESULT, item))
                                            .join("、") || DEFAULT_VALUE}
                                    </span>
                                }
                            />
                            <ZhBaseItem
                                label="实际货仓状态"
                                value={
                                    <span>
                                        {warehouseStateText(detail.actualWarehouseState, detail.remainingLayTime)}
                                    </span>
                                }
                            />
                        </ZhGrid>
                        <div className="mb-4 mt-6 text-base font-medium">运单信息</div>
                        <ZhGrid cols={3} gap={[30, 20]}>
                            <ZhBaseItem label="船运单">
                                <Link
                                    to={`/logistics/shippingNeed/waybillManagementInfo/${detail.transportOrderShipId}`}
                                >
                                    {text(detail.transportOrderShipId)}
                                </Link>
                            </ZhBaseItem>
                            <ZhBaseItem label="船舶">
                                <Link to={`/logistics/shipManage/shipInfo/shipInfoDetail/${detail.shipId}`}>
                                    {text(detail.shipCnName || detail.shipName)}
                                </Link>
                            </ZhBaseItem>
                            <ZhBaseItem
                                label="运单状态"
                                value={<span>{enumText(WAYBILL_STATE, detail.transportOrderShipState)}</span>}
                            />
                        </ZhGrid>
                        <MonitorWarnLocationCard detail={detail} />
                        <div className="mt-4">
                            <h3>预警画面</h3>
                            {image ? (
                                <img
                                    aria-label="预警画面"
                                    src={image}
                                    alt="预警画面"
                                    className="max-h-64 max-w-md rounded"
                                />
                            ) : (
                                <span>暂无图片</span>
                            )}
                        </div>
                    </>
                ) : (
                    <MonitorWarnLocationCard detail={detail} />
                )}
                {detail.handleState !== HANDLE_STATE.UNHANDLED.code ? (
                    <>
                        <div className="mb-4 mt-6 text-base font-medium">处理信息</div>
                        <ZhGrid cols={3} gap={[30, 20]}>
                            <ZhBaseItem label="处理人" value={<span>{text(detail.handlerName)}</span>} />
                            <ZhBaseItem
                                label="处理时间"
                                value={<span>{text(detail.handleTime?.replace("T", " "))}</span>}
                            />
                            <ZhBaseItem
                                label="是否误报"
                                value={<span>{detail.misinformation === 1 ? "是" : "否"}</span>}
                            />
                            <ZhBaseItem label="备注" span={3} value={<span>{text(detail.remark)}</span>} />
                        </ZhGrid>
                    </>
                ) : null}
                <Space className="mt-4">
                    {canAssign && detail.handleState === HANDLE_STATE.UNHANDLED.code ? (
                        <Button
                            aria-label="指派"
                            disabled={banButton}
                            onClick={() => {
                                setSelectedOfficer(undefined);
                                if (loadCommissioners) void loadCommissioners().then(setOfficers);
                                setAssignOpen(true);
                            }}
                        >
                            指派
                        </Button>
                    ) : null}
                    {canHandle ? (
                        <Button
                            aria-label="处理"
                            disabled={banButton}
                            onClick={() => {
                                setRemark("");
                                setMisinformation(0);
                                setHandleError("");
                                setHandleOpen(true);
                            }}
                        >
                            处理
                        </Button>
                    ) : null}
                    <Button onClick={() => navigate(-1)}>返回</Button>
                </Space>
            </Card>
            <Modal
                title="指派专员"
                className="assign-dialog-modal"
                open={assignOpen}
                okText="确定指派"
                cancelText="取消"
                confirmLoading={loading}
                onCancel={() => setAssignOpen(false)}
                onOk={() => void submitAssign()}
            >
                <div aria-label="专员列表" className="assign-dialog-list--simple" role="radiogroup">
                    {officers.map(item => (
                        <Button
                            key={item.user.id}
                            type={selectedOfficer === item.user.id ? "primary" : "default"}
                            onClick={() => setSelectedOfficer(item.user.id)}
                        >
                            {item.user.name}
                        </Button>
                    ))}
                </div>
            </Modal>
            <Modal
                title="处理"
                open={handleOpen}
                okText="提交处理"
                cancelText="取消"
                confirmLoading={loading}
                onCancel={() => setHandleOpen(false)}
                onOk={() => void submitHandle()}
            >
                {handleError ? <Alert role="alert" message={handleError} type="error" showIcon /> : null}
                <label>
                    是否误报
                    <Select
                        aria-label="是否误报"
                        value={misinformation}
                        options={[
                            { value: 0, label: "否" },
                            { value: 1, label: "是" }
                        ]}
                        onChange={setMisinformation}
                    />
                </label>
                <label className="mt-3 block">
                    备注
                    <textarea aria-label="备注" value={remark} onChange={event => setRemark(event.target.value)} />
                </label>
            </Modal>
        </section>
    );
}
